import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { syncStoredConsent, trackPageView } from '../utils/analytics';

/**
 * Sends a GA4 page view on every react-router navigation, plus the first one.
 *
 * index.html configures the tag with send_page_view: false, so this hook is the
 * only source of page views — a route change is not a document load and would
 * otherwise go unrecorded.
 *
 * Pass enabled: false to leave a route out of the reports — the admin portal is
 * staff traffic and would only dilute the site's numbers.
 */
export function useAnalyticsPageViews(enabled = true): void {
  const location = useLocation();
  const consentSynced = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    /*
     * The stored consent has to reach the tag before the first page view,
     * otherwise that view is dropped by the denied defaults. A ref rather than
     * a separate effect: effect order would be fragile to reorder later.
     */
    if (!consentSynced.current) {
      syncStoredConsent();
      consentSynced.current = true;
    }

    /*
     * The pathname alone would collapse ?utm_source and friends, which is how
     * campaign traffic is attributed, so the search string goes along too.
     */
    trackPageView(`${location.pathname}${location.search}`);
  }, [enabled, location.pathname, location.search]);
}
